import { FC, Fragment, ReactElement, RefObject, TouchEventHandler, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import classnames from 'classnames';

import { useServerEnv } from '@hh.ru/magritte-common-is-server-env';
import { disableScroll } from '@hh.ru/magritte-common-modal-helper';
import { useCloseByClickOutside } from '@hh.ru/magritte-common-mouse';
import { SwipeEvent, useSwipe } from '@hh.ru/magritte-common-use-swipe';
import { AlertProps } from '@hh.ru/magritte-ui-alert/types';
import { useBreakpoint } from '@hh.ru/magritte-ui-breakpoint';
import { Layer } from '@hh.ru/magritte-ui-layer';

import styles from './alert.less';

type AnimationTimeout = { enter: number; exit: number };

type AnimationComponentProps = {
    animationTimeout: AnimationTimeout;
    visible: boolean;
    render: (animatedElementRef: RefObject<HTMLDivElement>, props?: JSX.IntrinsicElements['div']) => ReactElement;
};

const OPACITY_ANIMATION_CLASSES = {
    enter: classnames(styles.opacityAnimation, styles.enterAnimation),
    enterActive: classnames(styles.enterAnimationActive, styles.opacityAnimation),
    exit: classnames(styles.opacityAnimation, styles.exitAnimation),
    exitActive: classnames(styles.exitAnimationActive, styles.opacityAnimation),
};

const TRANSFORM_ANIMATION_CLASSES = {
    enterActive: styles.enterAnimationActive,
    exitActive: styles.exitAnimationActive,
};

const VIEWPORT_HEIGHT = (typeof window !== 'undefined' ? window.visualViewport?.height : null) ?? 0;
const SWIPE_THRESHOLD_REF = { current: { max: Math.round(VIEWPORT_HEIGHT * 0.8) } };

const toInteger = (value: string) => {
    const result = parseInt(value, 10);
    return !Number.isNaN(result) ? result : 0;
};

const withStopPropagation = (handler?: TouchEventHandler): TouchEventHandler => {
    return (event) => {
        event.stopPropagation();
        handler?.(event);
    };
};

export const OpacityAnimation: FC<AnimationComponentProps> = ({ animationTimeout, visible, render }) => {
    const animatedElementRef = useRef<HTMLDivElement | null>(null);

    return (
        <CSSTransition
            appear
            classNames={OPACITY_ANIMATION_CLASSES}
            in={visible}
            mountOnEnter
            nodeRef={animatedElementRef}
            timeout={animationTimeout}
            unmountOnExit
        >
            {render(animatedElementRef)}
        </CSSTransition>
    );
};

export const TransformAnimation: FC<AnimationComponentProps & { onClose: VoidFunction }> = ({
    animationTimeout,
    visible,
    render,
    onClose,
}) => {
    const animatedElementRef = useRef<HTMLDivElement | null>(null);
    const swipeOffsetRef = useRef(0);

    const runTranslateYAnimation = () => {
        if (animatedElementRef.current !== null && visualViewport !== null) {
            animatedElementRef.current.style.transform = `translateY(${
                visualViewport.height + swipeOffsetRef.current - animatedElementRef.current.getBoundingClientRect().top
            }px)`;
        }
    };

    const resetTranslateY = () => {
        if (animatedElementRef.current !== null) {
            animatedElementRef.current.style.transform = `translateY(${swipeOffsetRef.current}px)`;
        }
    };

    const resetSwipeOffset = () => {
        swipeOffsetRef.current = 0;
    };

    const swipeHandlers = useSwipe({
        thresholdYRef: SWIPE_THRESHOLD_REF,
        onSwipeMove: (event: SwipeEvent) => {
            if (animatedElementRef.current !== null) {
                animatedElementRef.current.style.transform = `translateY(${Math.max(event.distanceY, 0)}px)`;
            }
        },
        onSwipeEnd: (event: SwipeEvent) => {
            swipeOffsetRef.current = event.distanceY;
            onClose();
        },
        onSwipeCancel: () => {
            if (animatedElementRef.current !== null) {
                animatedElementRef.current.style.transform = `translateY(0px)`;
            }
        },
    });

    return (
        <CSSTransition
            appear
            classNames={TRANSFORM_ANIMATION_CLASSES}
            in={visible}
            mountOnEnter
            nodeRef={animatedElementRef}
            onEnter={runTranslateYAnimation}
            onEntering={resetTranslateY}
            onExit={resetTranslateY}
            onExiting={runTranslateYAnimation}
            onExited={resetSwipeOffset}
            timeout={animationTimeout}
            unmountOnExit
        >
            {render(animatedElementRef, {
                ...swipeHandlers,
                onTouchStart: withStopPropagation(swipeHandlers.onTouchStart),
                onTouchMove: withStopPropagation(swipeHandlers.onTouchMove),
            })}
        </CSSTransition>
    );
};

export const Alert: FC<AlertProps> = ({
    iconStyle = 'neutral',
    layout,
    icon,
    title,
    description,
    buttons,
    visible,
    onClose,
}) => {
    const [animationTimeout, setAnimationTimeout] = useState<AnimationTimeout | null>(null);
    const clickElementRef = useRef<HTMLDivElement | null>(null);
    const { isMobile } = useBreakpoint();
    const isServerEnv = useServerEnv();

    useEffect(() => {
        const animationTimeoutElement = document.createElement('div');
        animationTimeoutElement.classList.add(styles.animationTimeout);
        document.body.appendChild(animationTimeoutElement);
        const style = window.getComputedStyle(animationTimeoutElement);
        const enter = toInteger(style.getPropertyValue('--enter-animation-duration'));
        const exit = toInteger(style.getPropertyValue('--exit-animation-duration'));
        setAnimationTimeout({ enter, exit });
        document.body.removeChild(animationTimeoutElement);
    }, [setAnimationTimeout]);

    useEffect(() => {
        if (visible) {
            return disableScroll();
        }

        return void 0;
    }, [visible]);

    useCloseByClickOutside(clickElementRef, onClose, visible);

    if (!animationTimeout || isServerEnv) {
        return null;
    }

    const content = (
        <Fragment>
            <div className={styles.main} data-qa="magritte-alert">
                {icon && (
                    <div className={classnames(styles.icon, { [styles[`icon_${iconStyle}`]]: iconStyle })}>{icon}</div>
                )}
                <div className={styles.title}>{title}</div>
                {description && <div className={styles.description}>{description}</div>}
            </div>
            <div className={classnames(styles.buttons, { [styles[`buttons_${layout}`]]: layout })}>{buttons}</div>
        </Fragment>
    );

    if (isMobile) {
        return createPortal(
            <Fragment>
                <OpacityAnimation
                    animationTimeout={animationTimeout}
                    visible={visible}
                    render={(animatedElementRef) => (
                        <Layer layer="bottom-sheet">
                            <div
                                className={classnames(styles.overlay, styles.animationTimeout)}
                                onClick={onClose}
                                ref={animatedElementRef}
                            />
                        </Layer>
                    )}
                />
                <TransformAnimation
                    animationTimeout={animationTimeout}
                    visible={visible}
                    render={(animatedElementRef, props) => (
                        <Layer layer="bottom-sheet">
                            <div
                                {...props}
                                className={classnames(styles.mobileContainer, styles.animationTimeout)}
                                ref={animatedElementRef}
                            >
                                {content}
                            </div>
                        </Layer>
                    )}
                    onClose={onClose}
                />
            </Fragment>,
            document.body
        );
    }

    return createPortal(
        <OpacityAnimation
            animationTimeout={animationTimeout}
            visible={visible}
            render={(animatedElementRef) => (
                <Layer layer="modal">
                    <div className={classnames(styles.overlay, styles.animationTimeout)} ref={animatedElementRef}>
                        <div className={classnames(styles.desktopContainer)} ref={clickElementRef}>
                            {content}
                        </div>
                    </div>
                </Layer>
            )}
        />,
        document.body
    );
};
